import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Star, AlertCircle, CheckCircle, Shield, MapPin, Tag } from "lucide-react";
import { PricingBreakdown } from "./pricing-breakdown";
import { DateRangeField } from "./date-range-field";
import { GuestsField } from "./guests-field";
import { GuestInfoModal } from "./guest-info-modal";
import { ContactActionLinks } from "@/components/contact/contact-action-links";
import { useBooking } from "@/hooks/use-booking-context";
import { useDirectCheckout } from "@/hooks/use-direct-checkout";
import { formatCurrency, formatDate, type PricingBreakdown as PricingData } from "@/lib/pricing-utils";
import { cn, formatContactPhone } from "@/lib/utils";
import { BALANCE_POLICY_SUMMARY, BOOK_DIRECT_SHORT, STRIPE_SECURE_SHORT } from "@shared/property-facts";
import { calculateNights, isDateInPast, formatDateInput } from "@/lib/date-utils";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import type { SiteInfoSettings } from "@/lib/seo-utils";
import { getPromoToken } from "@/lib/promo";
import { QuoteError, requestQuote, retryQuote } from "@/lib/quote-request";

export interface BookingWidgetProps {
  onOfferInputChange?: () => void;
  selectedOfferCoupon?: { code: string; selection: number };
  className?: string;
  /** stacked = tall card (default). inline = compact horizontal row on desktop. */
  layout?: "stacked" | "inline";
  /**
   * The home this widget books. A page that knows its home passes it so the
   * rate, reviews and quote can never fall back to the default home while the
   * shared booking context is empty (first paint, or right after Clear all).
   */
  propertyId?: string;
  propertySlug?: string;
  /** Open the calendar straight away when no dates are chosen (the booking lightbox). */
  autoOpenDates?: boolean;
  /** Lightbox requests already supply exact dates; page URL must not overwrite them. */
  hydrateFromUrl?: boolean;
}

interface Property {
  id: string;
  title: string;
  description: string;
  general_location: string;
  check_in_time: string;
  check_out_time: string;
  max_guests: number;
  bedrooms: number;
  bathrooms: number;
  rating: string;
  review_count: number;
  created_at: string;
  photos: any[];
  amenities: any[];
  base_rate?: string;
  regular_rate?: string | null;
  compare_at_rate?: string | null;
  minNights?: number;
  minNightsSchedule?: {
    nearMonths: number;
    nearMinNights: number;
    farMinNights: number;
    nearUntil: string;
  } | null;
}

export function BookingWidget({
  className,
  layout = "stacked",
  propertyId: propertyIdProp,
  propertySlug: propertySlugProp,
  autoOpenDates = false,
  hydrateFromUrl = true,
  selectedOfferCoupon,
  onOfferInputChange,
}: BookingWidgetProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const {
    checkIn,
    checkOut,
    guests,
    hasValidDates,
    getDateValidationErrors,
    resetBooking,
    setCheckIn,
    setCheckOut,
    updateBookingDates,
    setGuests,
    propertyId: contextPropertyId,
    propertySlug: contextPropertySlug,
  } = useBooking();
  const propertyId = propertyIdProp || contextPropertyId;
  const propertySlug = propertySlugProp || (propertyIdProp ? undefined : contextPropertySlug);
  const [checkoutQuoteFailure, setCheckoutQuoteFailure] = useState<{ key: string; error: Error } | null>(null);
  const quoteSelectionKey = JSON.stringify([propertySlug || propertyId, checkIn, checkOut, guests]);
  const handleCheckoutQuoteError = useCallback((error: Error) => {
    setCheckoutQuoteFailure({ key: quoteSelectionKey, error });
  }, [quoteSelectionKey]);
  const { isLoading: directCheckoutLoading, isGuestModalOpen, bookingData: directCheckoutData, beginCheckout, closeGuestModal } = useDirectCheckout({ propertyId, propertySlug, onQuoteError: handleCheckoutQuoteError });
  const isInline = layout === "inline";
  
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [announceText, setAnnounceText] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState("");
  // "Pay 50% now" option. Offered only when the server says these dates are eligible.
  const [paymentPlan, setPaymentPlan] = useState<'full' | 'split'>('full');
  const [fieldsResetKey, setFieldsResetKey] = useState(0);
  const [calendarOpenRequest, setCalendarOpenRequest] = useState(0);
  const hydratedFromUrlRef = useRef(false);

  // Hydrate dates / guests / coupon from abandoned-cart follow-up links once.
  // A one-shot ref keeps Clear all from fighting URL params on later renders.
  useEffect(() => {
    if (!hydrateFromUrl || typeof window === "undefined" || hydratedFromUrlRef.current) return;
    hydratedFromUrlRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const start = params.get("start");
    const end = params.get("end");
    const guestsParam = params.get("guests");
    const coupon = params.get("coupon");
    if (start) updateBookingDates(start, end || undefined);
    else if (end) setCheckOut(end);
    if (guestsParam && /^\d+$/.test(guestsParam)) setGuests(guestsParam);
    if (coupon) {
      const normalized = coupon.trim().toUpperCase();
      setCouponInput(normalized);
      setAppliedCoupon(normalized);
    }
  }, [updateBookingDates, setCheckOut, setGuests, hydrateFromUrl]);

  // Fetch site settings for dynamic contact info
  const { data: siteSettings } = useQuery<SiteInfoSettings>({
    queryKey: ['/api/settings/site_info'],
  });
  
  // Fetch booking settings (minimum nights, etc.) as backup
  const checkInForSettings = formatDateInput(checkIn) || "";
  const { data: bookingSettings } = useQuery<{
    minNights: number;
    minNightsSchedule?: {
      nearMonths: number;
      nearMinNights: number;
      farMinNights: number;
      nearUntil: string;
    } | null;
  }>({
    queryKey: [
      "/api/booking-settings",
      {
        ...(checkInForSettings ? { checkIn: checkInForSettings } : {}),
        ...(propertySlug || propertyId ? { property: propertySlug || propertyId } : {}),
      },
    ],
  });
  const contactPhone = formatContactPhone(siteSettings?.contact_phone);
  
  // Refs for focus management
  const formRef = useRef<HTMLFormElement>(null);
  const errorSummaryRef = useRef<HTMLDivElement>(null);

  // Enhanced validation functions using new date utilities
  const validateForm = useCallback(() => {
    const errors: { [key: string]: string } = {};
    
    // Use the comprehensive date validation from the context
    const dateValidationErrors = getDateValidationErrors();
    if (dateValidationErrors.length > 0) {
      // Map validation errors to form fields
      dateValidationErrors.forEach(error => {
        if (error.includes('Check-in')) {
          errors.checkIn = error;
        } else if (error.includes('Check-out')) {
          errors.checkOut = error;
        } else {
          // Generic date error, assign to both fields if not already assigned
          if (!errors.checkIn && (error.includes('required') || error.includes('invalid'))) {
            errors.checkIn = error;
          }
          if (!errors.checkOut && (error.includes('required') || error.includes('invalid'))) {
            errors.checkOut = error;
          }
        }
      });
    }
    
    // Validate guests with better error handling
    if (!guests || guests.trim() === '') {
      errors.guests = 'Number of guests is required';
    } else {
      const guestCount = parseInt(guests);
      if (isNaN(guestCount) || guestCount <= 0) {
        errors.guests = 'Please select a valid number of guests';
      } else if (guestCount > 16) { // Reasonable upper limit
        errors.guests = 'Maximum 16 guests allowed';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [checkIn, checkOut, guests, getDateValidationErrors]);
  
  // A deliberate offer selection uses the same server-validated coupon path as manual entry.
  useEffect(() => {
    if (!selectedOfferCoupon) return;
    const code = selectedOfferCoupon.code.trim().toUpperCase();
    setCouponInput(code);
    setAppliedCoupon(code);
  }, [selectedOfferCoupon]);

  // Listen for promo popup coupon application (e.g. STAY5 popup on /booking)
  useEffect(() => {
    const handleApplyCoupon = (e: Event) => {
      const code = (e as CustomEvent<{ code?: string }>).detail?.code;
      if (code) {
        onOfferInputChange?.();
        const normalized = code.trim().toUpperCase();
        setCouponInput(normalized);
        setAppliedCoupon(normalized);
      }
    };
    window.addEventListener("apply-stay5-coupon", handleApplyCoupon);
    return () => window.removeEventListener("apply-stay5-coupon", handleApplyCoupon);
  }, [onOfferInputChange]);

  // Re-validate live only after the user has already attempted to submit
  // NOTE: formErrors intentionally excluded from deps to avoid a self-referencing loop
  useEffect(() => {
    if (hasAttemptedSubmit) {
      validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIn, checkOut, guests, hasAttemptedSubmit]);

  // Get property data (public details for privacy)
  const { data: property, isLoading: propertyLoading } = useQuery<Property>({
    queryKey: propertySlug ? ["/api/properties", propertySlug] : ["/api/property/public", ...(propertyId ? [{ propertyId }] : [])],
    enabled: true,
  });

  // Date-aware minimum nights: use schedule when check-in is known.
  const schedule = bookingSettings?.minNightsSchedule ?? property?.minNightsSchedule ?? null;
  const minNights = (() => {
    if (schedule && checkInForSettings) {
      return checkInForSettings <= schedule.nearUntil
        ? schedule.nearMinNights
        : schedule.farMinNights;
    }
    return property?.minNights ?? bookingSettings?.minNights ?? 1;
  })();

  // Get actual review stats for dynamic display
  const { data: reviewSummary } = useQuery<{ averageRating: number; totalReviews: number }>({
    queryKey: ["/api/reviews/summary", propertySlug || propertyId ? { property: propertySlug || propertyId } : {}],
    enabled: true,
  });

  // Get pricing quote with improved error handling for minimum nights
  const { data: quoteData, isFetching: pricingLoading, error: queryPricingError, isSuccess: quoteSuccess, refetch: refetchPricing, dataUpdatedAt } = useQuery<PricingData>({
    queryKey: ['/api/quote', { start: checkIn, end: checkOut, guests, coupon: appliedCoupon, property: propertySlug || propertyId }],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({
        start: formatDateInput(checkIn) || '',
        end: formatDateInput(checkOut) || '',
        guests: guests || ''
      });
      if (propertySlug) params.set('property', propertySlug);
      else if (propertyId) params.set('propertyId', propertyId);
      if (appliedCoupon) {
        params.set('coupon', appliedCoupon);
      }
      // Attach the signed urgency token so the limited-time 10% (BOOK10) validates
      // server-side while its window is open. Harmless for any other coupon.
      const promoToken = getPromoToken();
      if (promoToken) {
        params.set('promo_token', promoToken);
      }
      return requestQuote(params, signal);
    },
    enabled: Boolean(hasValidDates() && guests && parseInt(guests) > 0),
    retry: retryQuote,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: query => query.state.error instanceof QuoteError && query.state.error.needsDateChange ? false : "always",
  });
  const pricingError = queryPricingError || (checkoutQuoteFailure?.key === quoteSelectionKey ? checkoutQuoteFailure.error : null);
  useEffect(() => { setCheckoutQuoteFailure(null); }, [dataUpdatedAt, quoteSelectionKey, appliedCoupon]);
  // React Query retains earlier data after a failed refetch. Never display or
  // submit it as a current quote, or use it while the new request is in flight.
  const pricing = quoteSuccess && !pricingError && !pricingLoading && hasValidDates() ? quoteData : undefined;
  const needsDateChange = pricingError instanceof QuoteError && pricingError.needsDateChange;
  
  // Announce pricing updates for screen readers
  useEffect(() => {
    if (pricing && !pricingLoading && !pricingError) {
      const nights = calculateNights(checkIn, checkOut);
      setAnnounceText(`Pricing updated: ${nights} nights at ${formatCurrency(pricing.nightlyRate)} per night, total ${formatCurrency(pricing.total)}`);
    } else if (pricingError) {
      // CRITICAL FIX: Announce specific minimum nights error
      if (pricingError instanceof QuoteError && pricingError.code === 'MINIMUM_NIGHTS_ERROR') {
        const requiredNights = pricingError.minNights || minNights;
        setAnnounceText(`Minimum stay requirement: This property requires at least ${requiredNights} nights. Please select a longer stay.`);
      } else {
        setAnnounceText('Pricing unavailable for selected dates. Please choose different dates.');
      }
    }
  }, [pricing, pricingLoading, pricingError, checkIn, checkOut, minNights]);



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);
    setIsSubmitting(true);
    
    // Validate form
    if (!validateForm()) {
      setIsSubmitting(false);
      setAnnounceText('Please fix the errors in the form before submitting.');
      
      // Focus on first error field with improved Select support
      const firstErrorField = formRef.current?.querySelector('[aria-invalid="true"]') as HTMLElement;
      if (firstErrorField) {
        // For Radix Select components, focus the button inside the SelectTrigger
        const selectButton = firstErrorField.querySelector('button[role="combobox"]') as HTMLElement;
        if (selectButton) {
          selectButton.focus();
        } else {
          firstErrorField.focus();
        }
      }
      
      // Focus on error summary for screen readers
      setTimeout(() => {
        if (errorSummaryRef.current) {
          errorSummaryRef.current.focus();
        }
      }, 100);
      
      return;
    }

    if (!pricing || pricingError || pricingLoading || directCheckoutLoading) {
      setIsSubmitting(false);
      const errorMessage = 'Unable to calculate pricing. Please try again.';
      setAnnounceText(errorMessage);
      toast({
        title: "Pricing Error",
        description: errorMessage,
        variant: "destructive",
      });
      return;
    }

    try {
      setAnnounceText('Processing your booking...');
      await beginCheckout(appliedCoupon || undefined, paymentPlan, property?.title);
    } catch (error) {
      const errorMessage = 'An error occurred while processing your booking. Please try again.';
      setAnnounceText(errorMessage);
      toast({
        title: "Booking Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Must be declared before any early returns to satisfy Rules of Hooks.
  const hasClearableInput = Boolean(
    checkIn ||
      checkOut ||
      (guests && guests !== "2") ||
      couponInput.trim() ||
      appliedCoupon ||
      paymentPlan !== "full",
  );

  const handleClearAll = useCallback(() => {
    onOfferInputChange?.();
    resetBooking();
    setCouponInput("");
    setAppliedCoupon("");
    setPaymentPlan("full");
    setFormErrors({});
    setHasAttemptedSubmit(false);
    setFieldsResetKey((key) => key + 1);
    setAnnounceText("Booking form cleared. Dates, guests, and coupon code have been reset.");
  }, [resetBooking, onOfferInputChange]);

  if (propertyLoading) {
    return (
      <section 
        className={`booking-form-panel rounded-2xl border border-sand-deep p-6 ${className}`}
        aria-label="Booking widget"
        aria-busy="true"
        data-testid="booking-widget"
      >
        <div className="flex items-center justify-center h-48" role="status" aria-live="polite">
          <Loader2 className="h-8 w-8 animate-spin text-ink" aria-hidden="true" />
          <span className="sr-only">Loading booking information...</span>
        </div>
      </section>
    );
  }

  // Handle property loading errors
  if (!property && !propertyLoading) {
    return (
      <section 
        className={`booking-form-panel rounded-2xl border border-sand-deep p-6 ${className}`}
        aria-label="Booking widget"
        data-testid="booking-widget"
      >
        <div className="text-center py-8">
          <AlertCircle className="h-8 w-8 text-ink mx-auto mb-4" />
          <h3 className="font-serif font-normal text-forest mb-2">Unable to load property information</h3>
          <p className="font-sans text-luxury-sm text-ink-soft mb-4">Please try refreshing the page or contact support if the issue persists.</p>
          <Button 
            onClick={() => window.location.reload()} 
            variant="outline"
            className="font-sans font-semibold border-sand-deep text-ink hover:bg-sand"
          >
            Refresh Page
          </Button>
        </div>
      </section>
    );
  }

  const fieldSurfaceClass = isInline ? undefined : "!bg-transparent hover:!bg-sand/60";
  const nights = checkIn && checkOut ? calculateNights(checkIn, checkOut) : 0;
  const baseRate = pricing?.nightlyRate ?? property?.base_rate ?? null;
  // Compare only the server-verified price of these same nights without the offer.
  const lastMinute = pricing?.appliedPromotion?.type === "last_minute_rate" ? pricing.appliedPromotion : null;
  const compareAtRate = lastMinute?.regularNightlyRate ?? null;
  const showCompareAt = (lastMinute ? true : !pricing?.advancePricing) && compareAtRate != null && parseFloat(compareAtRate) > parseFloat(baseRate ?? "0");
  const lastMinuteSaving = lastMinute?.regularSubtotal && pricing?.subtotal
    ? parseFloat(lastMinute.regularSubtotal) - parseFloat(pricing.subtotal)
    : 0;
  const lastMinuteNote = lastMinute && (
    <p className="text-[12px] font-sans text-forest mb-2" data-testid="last-minute-rate-applied">
      Last-minute rate{lastMinuteSaving > 0 ? `: you save ${formatCurrency(lastMinuteSaving.toFixed(2))} on these dates` : " applied"}
    </p>
  );

  // Error summary for screen readers with anchor links
  const errorSummary = hasAttemptedSubmit && Object.keys(formErrors).length > 0 && (
    <div 
      ref={errorSummaryRef}
      className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
      role="alert"
      aria-live="assertive"
      tabIndex={-1}
      id="error-summary"
      aria-labelledby="error-summary-title"
    >
      <div id="error-summary-title" className="flex items-center gap-2 text-destructive font-serif font-normal tracking-luxury-tight mb-2">
        <AlertCircle className="h-4 w-4" aria-hidden="true" />
        <span>Please correct the following errors:</span>
      </div>
      <ul className="text-luxury-sm font-serif font-normal text-destructive space-y-1" role="list">
        {Object.entries(formErrors).map(([field, message]) => (
          <li key={field}>
            <a 
              href={`#${field === 'checkIn' ? 'checkin' : field === 'checkOut' ? 'checkout' : field}`}
              className="text-destructive underline hover:text-destructive/80 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-2 rounded"
              onClick={(e) => {
                e.preventDefault();
                const fieldId = field === 'checkIn' ? 'checkin' : field === 'checkOut' ? 'checkout' : field;
                const fieldElement = document.getElementById(fieldId);
                if (fieldElement) {
                  fieldElement.focus();
                  fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }}
              data-testid={`error-link-${field}`}
            >
              {field === 'checkIn' && 'Check-in date: '}
              {field === 'checkOut' && 'Check-out date: '}
              {field === 'guests' && 'Number of guests: '}
              {message}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );

  const reserveButton = (
    <Button
      type="submit"
      className={cn(
        "bg-sun text-shell hover:bg-sun-hover font-sans font-semibold tracking-[0.08em] uppercase transition-transform duration-200 cursor-pointer shadow-[0_3px_14px_rgba(23,49,28,0.28)] hover:-translate-y-[2px] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:translate-y-0",
        isInline
          ? "w-full xl:w-auto xl:min-w-[140px] px-7 h-16 rounded-xl"
          : "w-full py-3 h-[50px] rounded-lg",
      )}
      disabled={!pricing || pricingLoading || isSubmitting || directCheckoutLoading || !hasValidDates()}
      data-testid="button-reserve"
      aria-describedby="reserve-button-help"
      aria-label={`Reserve property for ${nights > 0 ? nights + ' nights' : 'selected dates'}${pricing ? ' at ' + formatCurrency(pricing.total) + ' total' : ''}`}
    >
      {isSubmitting ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden="true" />
          Processing...
        </>
      ) : (
        "Reserve"
      )}
    </Button>
  );

  const couponMessages = (
    <>
      {appliedCoupon && !pricing?.coupon && pricing && !pricingLoading && (
        <p className="text-luxury-xs text-[#B33C22] font-sans" data-testid="coupon-invalid-message">
          {pricing?.couponError || `Coupon "${appliedCoupon}" could not be applied.`}
        </p>
      )}
      {pricing?.coupon && pricing.appliedDiscount === "coupon" && (
        <p className="text-luxury-xs text-palm font-sans font-medium" data-testid="coupon-applied-message">
          Coupon &quot;{pricing.coupon.code}&quot; applied
        </p>
      )}
      {pricing?.coupon && pricing.appliedDiscount === "long_stay" && (
        <p className="text-luxury-xs text-palm font-sans font-medium" data-testid="coupon-superseded-message">
          Good news: your stay qualifies for a larger {pricing.nights >= 30 ? "monthly" : "weekly"} discount, applied instead of &quot;{pricing.coupon.code}&quot;
        </p>
      )}
    </>
  );


  const clearAllControl = hasClearableInput ? (
    <div className={cn(isInline ? "pt-1" : "pt-0.5")}>
      <button
        type="button"
        onClick={handleClearAll}
        className="font-sans text-[12px] md:text-[13px] font-semibold uppercase tracking-[0.1em] text-ink-soft hover:text-ink transition-colors underline underline-offset-4 decoration-sand-deep hover:decoration-ink"
        data-testid="button-clear-booking-form"
      >
        Clear all
      </button>
    </div>
  ) : null;

  const contactActions = (
    <ContactActionLinks
      phone={contactPhone}
      email={siteSettings?.contact_email}
      variant="buttons"
      layout={isInline ? "row" : "stack"}
      surface={isInline ? "shell" : "sand"}
      testIdPrefix="booking"
      className={cn(
        "w-full min-w-0",
        isInline && "shrink-0 max-w-md lg:max-w-sm xl:max-w-md",
      )}
    />
  );

  return (
    <section 
      className={cn(
        isInline
          ? "bg-transparent rounded-none border-0 p-0"
          : "booking-form-panel rounded-2xl border border-sand-deep p-6",
        className,
      )}
      data-testid="booking-widget"
      data-layout={layout}
      aria-labelledby="booking-widget-title"
      role="region"
    >
      {/* Screen reader announcements */}
      <div 
        aria-live="polite" 
        aria-atomic="true"
        className="sr-only"
        role="status"
      >
        {announceText}
      </div>

      {!isInline && (
        <header className="mb-5">
          <h2 id="booking-widget-title" className="sr-only">Property Booking</h2>
          <div className="flex items-baseline gap-1.5 mb-1">
            {showCompareAt && (
              <span className="text-luxury-lg font-serif text-driftwood line-through decoration-1" data-testid="price-compare-at">
                {formatCurrency(compareAtRate!)}
              </span>
            )}
            <span className="text-luxury-2xl font-serif font-bold tracking-luxury-tight text-ink" data-testid="price-per-night">
              {baseRate ? formatCurrency(baseRate) : "Choose dates"}
            </span>
            <span className="text-ink-soft font-sans font-normal tracking-luxury-tight text-luxury-base">/ night · USD</span>
            {pricingLoading && (
              <Loader2 className="h-4 w-4 animate-spin text-ink-soft ml-2" aria-label="Loading pricing" />
            )}
          </div>
          {pricing && pricing.breakdown.filter(item => item.label.includes('×') && item.label.includes('night')).length > 1 && (
            <p className="text-[12px] font-sans text-ink-soft mb-2">Average nightly rate; see the nightly breakdown below.</p>
          )}
          <p className="text-xs text-ink-soft mb-2">{pricing ? "Selected dates · Average nightly rate before taxes & fees" : "Indicative rate · Choose dates for your quote"}</p>
          {lastMinuteNote}
          <div className="flex items-center text-luxury-sm font-sans font-normal text-ink-soft" role="group" aria-label="Property rating">
            <Star className="h-4 w-4 text-sun mr-1 fill-current" aria-hidden="true" />
            {reviewSummary && reviewSummary.totalReviews > 0 ? (
              <>
                <span data-testid="property-rating" role="img" aria-label={`Rating: ${reviewSummary.averageRating.toFixed(2)} out of 5 stars`}>
                  {reviewSummary.averageRating.toFixed(2)}
                </span>
                <span className="mx-1" aria-hidden="true">·</span>
                <span data-testid="review-count" aria-label={`${reviewSummary.totalReviews} guest reviews`}>
                  {reviewSummary.totalReviews} reviews
                </span>
              </>
            ) : (
              <span className="text-luxury-xs font-sans font-normal text-ink-soft">Not yet rated</span>
            )}
          </div>
        </header>
      )}

      <div>
        {errorSummary}

        <form 
          ref={formRef}
          onSubmit={handleSubmit} 
          className="space-y-3"
          role="form"
          aria-label="Hotel booking form"
          noValidate
        >
          {isInline ? (
            <>
              <h2 id="booking-widget-title" className="sr-only">Property Booking</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[auto_minmax(200px,1.4fr)_minmax(140px,0.9fr)_minmax(160px,1fr)_auto] gap-3 xl:gap-4 items-center">
                <div className="sm:col-span-2 xl:col-span-1 xl:pr-2 xl:min-w-[150px]">
                  <div className="flex items-baseline gap-1.5 flex-wrap">
                    {showCompareAt && (
                      <span className="text-luxury-base font-serif text-driftwood line-through decoration-1" data-testid="price-compare-at">
                        {formatCurrency(compareAtRate!)}
                      </span>
                    )}
                    <span className="text-luxury-xl font-serif font-bold tracking-luxury-tight text-ink" data-testid="price-per-night">
                      {baseRate ? formatCurrency(baseRate) : "Choose dates"}
                    </span>
                    <span className="text-ink-soft font-sans font-normal text-luxury-sm">/ night · USD</span>
                    {pricingLoading && (
                      <Loader2 className="h-4 w-4 animate-spin text-ink-soft" aria-label="Loading pricing" />
                    )}
                  </div>
                  {pricing && pricing.breakdown.filter(item => item.label.includes('×') && item.label.includes('night')).length > 1 && (
                    <p className="text-[12px] font-sans text-ink-soft mt-1">Average per night; see breakdown below.</p>
                  )}
                  <p className="text-xs text-ink-soft mb-2">{pricing ? "Selected dates · Average nightly rate before taxes & fees" : "Indicative rate · Choose dates for your quote"}</p>
                  {lastMinuteNote}
                  <div className="mt-1 flex items-center text-luxury-xs font-sans text-ink-soft" role="group" aria-label="Property rating">
                    <Star className="h-3.5 w-3.5 text-sun mr-1 fill-current" aria-hidden="true" />
                    {reviewSummary && reviewSummary.totalReviews > 0 ? (
                      <>
                        <span data-testid="property-rating">{reviewSummary.averageRating.toFixed(2)}</span>
                        <span className="mx-1" aria-hidden="true">·</span>
                        <span data-testid="review-count">{reviewSummary.totalReviews} reviews</span>
                      </>
                    ) : (
                      <span>Not yet rated</span>
                    )}
                  </div>
                </div>

                <DateRangeField
                  key={`dates-${fieldsResetKey}`}
                  error={hasAttemptedSubmit && !!(formErrors.checkIn || formErrors.checkOut)}
                  minNights={minNights}
                  minNightsSchedule={schedule}
                  triggerClassName={fieldSurfaceClass}
                  propertySlug={propertySlug || propertyId}
                  openRequest={calendarOpenRequest}
                />

                <GuestsField
                  key={`guests-${fieldsResetKey}`}
                  error={hasAttemptedSubmit && !!formErrors.guests}
                  triggerClassName={fieldSurfaceClass}
                />

                <div className="flex gap-2 min-w-0 h-16">
                  <Input
                    id="coupon-code"
                    type="text"
                    placeholder="Coupon code"
                    value={couponInput}
                    onChange={(e) => {onOfferInputChange?.();setCouponInput(e.target.value.toUpperCase());}}
                    className="font-sans text-luxury-sm border-sand-deep bg-shell focus:border-ink focus:ring-1 focus:ring-ink h-full rounded-xl px-4"
                    data-testid="input-coupon-code"
                    aria-label="Coupon code"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="font-sans font-semibold shrink-0 border-sand-deep text-ink hover:bg-shell h-full rounded-xl px-4"
                    disabled={!couponInput.trim() || pricingLoading}
                    onClick={() => {onOfferInputChange?.();setAppliedCoupon(couponInput.trim().toUpperCase());}}
                    data-testid="button-apply-coupon"
                  >
                    Apply
                  </Button>
                </div>

                <div className="sm:col-span-2 xl:col-span-1 h-16 flex">
                  {reserveButton}
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="space-y-1 min-w-0 flex-1">{couponMessages}</div>
                {clearAllControl}
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <DateRangeField
                key={`dates-${fieldsResetKey}`}
                error={hasAttemptedSubmit && !!(formErrors.checkIn || formErrors.checkOut)}
                minNights={minNights}
                minNightsSchedule={schedule}
                triggerClassName={fieldSurfaceClass}
                autoOpen={autoOpenDates}
                propertySlug={propertySlug || propertyId}
                openRequest={calendarOpenRequest}
              />
              
              <GuestsField
                key={`guests-${fieldsResetKey}`}
                error={hasAttemptedSubmit && !!formErrors.guests}
                triggerClassName={fieldSurfaceClass}
              />

              <div className="space-y-1.5">
                <Label htmlFor="coupon-code" className="text-luxury-sm font-sans font-medium text-ink-soft">
                  Coupon code
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="coupon-code"
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponInput}
                    onChange={(e) => {onOfferInputChange?.();setCouponInput(e.target.value.toUpperCase());}}
                    className="font-sans text-luxury-sm border-sand-deep !bg-transparent focus:border-ink focus:ring-1 focus:ring-ink"
                    data-testid="input-coupon-code"
                    aria-label="Coupon code"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="font-sans font-semibold shrink-0 border-sand-deep text-ink hover:bg-sand"
                    disabled={!couponInput.trim() || pricingLoading}
                    onClick={() => {onOfferInputChange?.();setAppliedCoupon(couponInput.trim().toUpperCase());}}
                    data-testid="button-apply-coupon"
                  >
                    Apply
                  </Button>
                </div>
                {couponMessages}
              </div>

              {clearAllControl}
            </div>
          )}

          {/* Split payment. Shown at the point of sticker shock: a full week is a large
              single charge, so offering half now materially lowers the barrier. */}
          {pricing?.paymentPlan?.splitEligible && !pricingLoading && (
            <div className="space-y-2" data-testid="payment-plan-options">
              <p className="text-luxury-sm font-sans font-semibold text-ink">How would you like to pay?</p>
              <div className={cn("grid gap-2", isInline ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1")}>
                <button
                  type="button"
                  onClick={() => setPaymentPlan('full')}
                  aria-pressed={paymentPlan === 'full'}
                  className={cn(
                    "text-left rounded-lg border p-3 transition-colors min-h-[44px]",
                    paymentPlan === 'full'
                      ? "border-ink bg-transparent shadow-[0_0_0_1px_#17311C]"
                      : "border-sand-deep bg-transparent hover:border-ink"
                  )}
                  data-testid="option-pay-full"
                >
                  <span className="block font-sans font-semibold text-luxury-sm text-ink">
                    Pay in full today
                  </span>
                  <span className="block text-luxury-xs text-ink-soft">
                    {formatCurrency(pricing.total)} now
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentPlan('split')}
                  aria-pressed={paymentPlan === 'split'}
                  className={cn(
                    "text-left rounded-lg border p-3 transition-colors min-h-[44px]",
                    paymentPlan === 'split'
                      ? "border-ink bg-transparent shadow-[0_0_0_1px_#17311C]"
                      : "border-sand-deep bg-transparent hover:border-ink"
                  )}
                  data-testid="option-pay-split"
                >
                  <span className="block font-sans font-semibold text-luxury-sm text-ink">
                    Pay 50% now, rest later
                  </span>
                  <span className="block text-luxury-xs text-ink-soft">
                    {formatCurrency(pricing.paymentPlan.depositAmount || '0')} today, {formatCurrency(pricing.paymentPlan.balanceAmount || '0')} due{' '}
                    {pricing.paymentPlan.balanceDueDate ? formatDate(pricing.paymentPlan.balanceDueDate) : 'before arrival'}
                  </span>
                </button>
              </div>
              {/* Stated before any money is taken, so the consequence of missing the
                  balance is never a surprise. */}
              {paymentPlan === 'split' && (
                <p className="text-luxury-xs text-ink-soft font-sans" data-testid="balance-policy-note">
                  {BALANCE_POLICY_SUMMARY}
                </p>
              )}
            </div>
          )}

          {!isInline && (
            <div className="pt-1">
              {reserveButton}
            </div>
          )}
          <div id="reserve-button-help" className="sr-only">
            Click to proceed to guest details and payment. You will not be charged until you complete your booking.
          </div>
        </form>
      </div>

      {!isInline && (
        <div className="mt-3">
          <div className="text-center text-luxury-sm font-sans font-medium text-ink-soft" role="note" aria-label="Payment notice">
            <CheckCircle className="h-4 w-4 inline mr-1.5 text-palm" aria-hidden="true" />
            You won&apos;t be charged yet
          </div>
        </div>
      )}

      {/* Trust strip underneath the booking controls */}
      <div
        className={cn("border-t border-sand-deep", isInline ? "mt-4 pt-4" : "mt-6 pt-5")}
        data-testid="trust-signals"
      >
        {isInline ? (
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-luxury-xs font-sans text-ink-soft">
              <p className="flex items-center font-medium" role="note">
                <CheckCircle className="h-3.5 w-3.5 mr-1.5 text-palm" aria-hidden="true" />
                You won&apos;t be charged yet
              </p>
              <div className="flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5 text-ink flex-shrink-0" aria-hidden="true" />
                <span>Verified beachfront</span>
              </div>
              {reviewSummary && reviewSummary.totalReviews > 0 && (
                <div className="flex items-center gap-2">
                  <Star className="h-3.5 w-3.5 text-ink fill-ink flex-shrink-0" aria-hidden="true" />
                  <span>{reviewSummary.averageRating.toFixed(2)} · {reviewSummary.totalReviews} reviews</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Shield className="h-3.5 w-3.5 text-ink flex-shrink-0" aria-hidden="true" />
                <span>{STRIPE_SECURE_SHORT}</span>
              </div>
              <div className="flex items-center gap-2">
                <Tag className="h-3.5 w-3.5 text-ink flex-shrink-0" aria-hidden="true" />
                <span>{BOOK_DIRECT_SHORT}</span>
              </div>
            </div>
            {contactActions}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-luxury-xs font-sans text-ink-soft">
            <div className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-ink flex-shrink-0" aria-hidden="true" />
              <span>Verified beachfront</span>
            </div>
            {reviewSummary && reviewSummary.totalReviews > 0 && (
              <div className="flex items-center gap-2">
                <Star className="h-3.5 w-3.5 text-ink fill-ink flex-shrink-0" aria-hidden="true" />
                <span>{reviewSummary.averageRating.toFixed(2)} · {reviewSummary.totalReviews} reviews</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Shield className="h-3.5 w-3.5 text-ink flex-shrink-0" aria-hidden="true" />
              <span>{STRIPE_SECURE_SHORT}</span>
            </div>
            <div className="flex items-center gap-2">
              <Tag className="h-3.5 w-3.5 text-ink flex-shrink-0" aria-hidden="true" />
              <span>{BOOK_DIRECT_SHORT}</span>
            </div>
            <div className="col-span-2 flex items-center gap-2 pt-1">
              {contactActions}
            </div>
          </div>
        )}
      </div>

      {/* Full price breakdown: every line shown inline (no hidden totals) */}
      {pricing && pricing.breakdown && !pricingError && (
        <div className={cn("border-t border-sand-deep", isInline ? "mt-4 pt-4" : "mt-6 pt-5")} data-testid="widget-pricing-breakdown">
          {pricing.advancePricing && (
            <p className="text-luxury-xs font-sans text-forest mb-3" data-testid="advance-rate-applied">
              Standard nights are {pricing.advancePricing.percent}% higher for check-in {pricing.advancePricing.tier * 6}+ months ahead. Seasonal offers are unchanged.
            </p>
          )}
          <PricingBreakdown pricing={pricing} />
        </div>
      )}

      {pricingError && nights > 0 && (
        <div data-testid="quote-error" role="alert" className="mt-4 p-4 bg-destructive/10 text-destructive rounded-lg text-luxury-sm font-serif font-normal border border-destructive/20">
          <div className="flex items-center justify-between">
            <div>
              {/* CRITICAL FIX: Show specific message for minimum nights errors */}
              {pricingError instanceof QuoteError && pricingError.code === 'MINIMUM_NIGHTS_ERROR' ? (
                <>
                  <p className="font-normal text-luxury-sm">Minimum Stay Required</p>
                  <p className="text-luxury-xs text-destructive/80 mt-1">
                    {pricingError.message || `This property requires a minimum stay of ${minNights} nights.`}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-normal text-luxury-sm">{needsDateChange ? "Selected dates are unavailable" : "Unable to calculate pricing"}</p>
                  <p className="text-luxury-xs text-destructive/80 mt-1">
                    {needsDateChange ? `Please choose different dates for ${property?.title || "this home"}.` : "A connection or service error prevented pricing. Please try again."}
                  </p>
                </>
              )}
            </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid={needsDateChange ? "change-dates" : "retry-quote"}
                disabled={pricingLoading}
                onClick={() => needsDateChange ? setCalendarOpenRequest(n => n + 1) : refetchPricing()}
                className="text-destructive hover:text-destructive/80 font-serif font-normal"
              >
                {needsDateChange ? "Change dates" : pricingLoading ? "Retrying…" : "Retry pricing"}
              </Button>
          </div>
        </div>
      )}

      {/* Guest Info Modal for direct checkout */}
      <GuestInfoModal
        isOpen={isGuestModalOpen}
        onClose={closeGuestModal}
        bookingData={directCheckoutData}
      />
    </section>
  );
}
